/*
 * Inch of Metal — "sheared size" matcher (theme-side correction)
 * ---------------------------------------------------------------------------
 * Cut-to-size sheet/plate products use two independent apps on the same page:
 *
 *   1. DPO / Itoris ("Dynamic Product Options")  -> renders the Custom Width and
 *      Custom Length dropdowns (.width select / .len select) and attaches the
 *      entered dimensions to the order as line-item properties.
 *   2. BCPO / Best Custom Product Options        -> renders the "Sheared Size"
 *      dropdown (.single-option-selector), which is what actually selects the
 *      Shopify variant used for price, weight and shipping.
 *
 * DPO's option template (template 43170) carries an "Extra JS" snippet that is
 * supposed to bridge the two: read the entered dimensions, then select the
 * matching Sheared Size. That snippet has two defects, both identical to the
 * ones found on the All Metals store:
 *
 *   A. It matches on AREA only ( width * length ), never checking that the
 *      piece actually fits inside the sheet. Confirmed live on this store:
 *        20" x 20"  ->  picks 12" x 36"  (should be 24" x 24",  $7.51 under)
 *        13" x 13"  ->  picks  6" x 30"  (should be 18" x 18",  $7.51 under)
 *         7" x 7"   ->  picks  6" x 12"  (should be 12" x 12",  $3.74 under)
 *         1" x 36"  ->  picks  6" x 6"   (should be  6" x 36",  $9.39 under)
 *      Every one of those is also physically impossible to cut.
 *
 *   B. If the Sheared Size list is not in the DOM at the moment the shopper
 *      chooses a dimension (the two apps load independently, so this is a
 *      race), the loop matches nothing and exits silently. The size is left on
 *      the product's first entry — 6" x 6" — while the entered dimensions still
 *      ride along as line-item properties. That is the "cart should have
 *      triggered 24x30, instead it triggered 6x6" report.
 *
 * This file re-does the match correctly and never fails silently: it waits for
 * both apps, picks the smallest sheared size the piece genuinely fits inside
 * (either orientation), and re-runs whenever either app re-renders. It also
 * re-asserts shortly after each change, because DPO's own (incorrect) handler
 * fires on the same event and would otherwise overwrite the correct answer.
 *
 * The permanent home for this logic is the DPO option template's "Extra JS"
 * field — the code below is deliberately dependency-free so it can be pasted
 * there verbatim once the app side is ready to be edited.
 *
 * Add ?iomdebug=1 to a product URL to watch it work in the browser console.
 */
(function () {
  'use strict';

  var DEBUG = /[?&]iomdebug=1/.test(window.location.search);
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[iom-size-matcher]'].concat([].slice.call(arguments)));
  }

  var SEL_WIDTH = '.width input';
  var SEL_LENGTH = '.len input';
  /* This store renders Sheared Size as a <select>. The radio selector is kept so
     the same file keeps working if BCPO is ever switched to button/radio mode. */
  var SEL_SIZES = '.bcpo-front-dd-option, .single-option-selector option';

  /* "24\" (24.00\")" -> 24 ; "24\"-1/2\" (24.5\")" -> 24.5 */
  function readDimension(input) {
    if (!input) return NaN;
    var value = String(input.value || '');
    var parenthesised = value.match(/\(\s*([0-9]*\.?[0-9]+)/);
    if (parenthesised) return parseFloat(parenthesised[1]);
    var leading = value.match(/([0-9]*\.?[0-9]+)/);
    return leading ? parseFloat(leading[1]) : NaN;
  }

  /* "6\" x 36\"" -> { w: 6, l: 36 }  (also tolerates a trailing suffix) */
  function readSheet(el) {
    var match = String(el.value || '').match(/([0-9]*\.?[0-9]+)\s*"?\s*x\s*([0-9]*\.?[0-9]+)/i);
    if (!match) return null;
    return { w: parseFloat(match[1]), l: parseFloat(match[2]), el: el };
  }

  /* A piece fits a sheet if it fits in either orientation. */
  function fits(pieceW, pieceL, sheet) {
    return (pieceW <= sheet.w && pieceL <= sheet.l) || (pieceW <= sheet.l && pieceL <= sheet.w);
  }

  function chooseSheet(pieceW, pieceL, sheets) {
    var best = null;
    for (var i = 0; i < sheets.length; i++) {
      if (!fits(pieceW, pieceL, sheets[i])) continue;
      var area = sheets[i].w * sheets[i].l;
      if (!best || area < best.area) best = { area: area, sheet: sheets[i] };
    }
    /* Nothing large enough: fall back to the largest sheet on offer rather than
       leaving the shopper on whatever happened to be selected before. */
    if (!best && sheets.length) {
      var largest = sheets[0];
      for (var j = 1; j < sheets.length; j++) {
        if (sheets[j].w * sheets[j].l > largest.w * largest.l) largest = sheets[j];
      }
      return { sheet: largest, oversize: true };
    }
    return best ? { sheet: best.sheet, oversize: false } : null;
  }

  function isChosen(el) {
    return el.tagName === 'OPTION' ? el.selected : el.checked;
  }

  function choose(el) {
    if (el.tagName === 'OPTION') {
      var select = el.parentNode;
      while (select && select.tagName !== 'SELECT') select = select.parentNode;
      if (!select) return;
      select.value = el.value;
      el.selected = true;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      /* BCPO and the Supply theme both listen for jQuery-namespaced events,
         which a native dispatch does not reach. Nudge jQuery too when present. */
      if (window.jQuery) window.jQuery(select).trigger('change');
      return;
    }
    el.click();
    if (!el.checked) {
      el.checked = true;
      el.dispatchEvent(new Event('change', { bubbles: true }));
      if (window.jQuery) window.jQuery(el).trigger('change');
    }
  }

  function applyMatch(reason) {
    var widthInput = document.querySelector(SEL_WIDTH);
    var lengthInput = document.querySelector(SEL_LENGTH);
    if (!widthInput || !lengthInput) return false;

    var pieceW = readDimension(widthInput);
    var pieceL = readDimension(lengthInput);
    if (!isFinite(pieceW) || !isFinite(pieceL) || pieceW <= 0 || pieceL <= 0) {
      log('dimensions not ready', widthInput.value, lengthInput.value);
      return false;
    }

    var candidates = [].slice.call(document.querySelectorAll(SEL_SIZES));
    var sheets = [];
    for (var i = 0; i < candidates.length; i++) {
      var sheet = readSheet(candidates[i]);
      if (sheet) sheets.push(sheet);
    }
    if (!sheets.length) {
      /* The size list has not rendered yet. Do NOT give up quietly —
         retrying here is the whole point of this file. */
      log('sheared sizes not in DOM yet, will retry:', reason);
      return false;
    }

    var choice = chooseSheet(pieceW, pieceL, sheets);
    if (!choice) return false;
    if (choice.oversize) log('no sheared size fits', pieceW, pieceL, '- falling back to largest');

    if (!isChosen(choice.sheet.el)) {
      log(reason, pieceW + '" x ' + pieceL + '" ->', choice.sheet.el.value);
      choose(choice.sheet.el);
    }
    return true;
  }

  /* Retry until both apps have rendered. Backs off, then stops. */
  function applyWithRetry(reason) {
    var attempts = 0;
    (function attempt() {
      if (applyMatch(reason)) return;
      if (++attempts > 40) return log('gave up after', attempts, 'attempts:', reason);
      setTimeout(attempt, attempts < 10 ? 150 : 500);
    })();
  }

  var pending = null;
  function schedule(reason) {
    clearTimeout(pending);
    pending = setTimeout(function () {
      applyWithRetry(reason);
      /* DPO's own handler fires on the same change event and will write the
         wrong size. Re-assert twice so ours is the answer that survives.
         applyMatch is idempotent, so these are no-ops once it agrees. */
      setTimeout(function () { applyMatch(reason + ' (re-assert 1)'); }, 250);
      setTimeout(function () { applyMatch(reason + ' (re-assert 2)'); }, 900);
    }, 30);
  }

  /* React to the shopper changing either dimension. Bound on the document so it
     survives DPO tearing down and rebuilding its fields. */
  document.addEventListener(
    'change',
    function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      if (el.closest('.width') || el.closest('.len')) schedule('dimension changed');
    },
    true
  );

  /* React to either app rendering (or re-rendering) its controls. */
  var observerScheduled = false;
  new MutationObserver(function () {
    if (observerScheduled) return;
    observerScheduled = true;
    setTimeout(function () {
      observerScheduled = false;
      if (document.querySelector(SEL_WIDTH) && document.querySelector(SEL_SIZES)) schedule('dom changed');
    }, 120);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { schedule('initial'); });
  } else {
    schedule('initial');
  }
})();
